import { useEffect, useState } from "react";
import { wymhDefaultConfig, type WymhSiteConfig } from "@/mocks/wymh-config";

const CACHE_KEY = "wymh_site_config_cache_v1";

function resolveConfigUrl(): string {
  const base = String(__BASE_PATH__ || "/").replace(/\/+$/, "");
  return `${base}/wymh-config.json`;
}

function mergeConfig(input: Partial<WymhSiteConfig> | null | undefined): WymhSiteConfig {
  const data = input || {};
  return {
    ...wymhDefaultConfig,
    ...data,
    downloadLinks: {
      ...wymhDefaultConfig.downloadLinks,
      ...(data.downloadLinks || {}),
    },
  };
}

/**
 * 从外置 JSON（public/wymh-config.json）读取站点配置。
 * 更换下载链接或联系邮箱只需要改这个 JSON 文件，无需改源码、无需重新打包。
 */
export function useWymhSiteConfig(): WymhSiteConfig {
  const [config, setConfig] = useState<WymhSiteConfig>(wymhDefaultConfig);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const res = await fetch(resolveConfigUrl(), { cache: "no-store" });
        if (!res.ok) {
          throw new Error(`config status ${res.status}`);
        }
        const data = (await res.json()) as Partial<WymhSiteConfig>;
        if (!alive) return;
        const merged = mergeConfig(data);
        setConfig(merged);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
        } catch {
          /* 忽略本地缓存写入失败 */
        }
      } catch {
        try {
          const raw = localStorage.getItem(CACHE_KEY);
          if (raw && alive) {
            setConfig(mergeConfig(JSON.parse(raw)));
          }
        } catch {
          /* 使用默认配置 */
        }
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []);

  return config;
}